import { RefreshCw, AlertTriangle, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/shared/Loading'
import { RiskIndicator } from './RiskIndicator'
import type { ChurnAnalysis as ChurnAnalysisType } from '@/types'
import { formatDateTime } from '@/utils/formatDate'

interface ChurnAnalysisProps {
  analysis: ChurnAnalysisType | null
  onAnalyze: () => void
  loading: boolean
}

export function ChurnAnalysis({
  analysis,
  onAnalyze,
  loading,
}: ChurnAnalysisProps) {
  if (loading && !analysis) {
    return <Loading text="이탈 위험 분석 중..." />
  }

  if (!analysis) {
    return (
      <div className="text-center">
        <p className="mb-4 text-muted-foreground">
          이탈 위험을 분석하려면 클릭하세요
        </p>
        <Button onClick={onAnalyze} disabled={loading}>
          <TrendingUp className="mr-2 h-4 w-4" />
          분석
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Risk Level */}
      <div className="flex items-center justify-between">
        <RiskIndicator level={analysis.risk_level} score={analysis.risk_score} />
        <Button
          variant="ghost"
          size="sm"
          onClick={onAnalyze}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Days Inactive */}
      <div className="rounded-lg bg-muted p-3">
        <p className="text-sm text-muted-foreground">비활성 일수</p>
        <p className="text-2xl font-bold">{analysis.days_inactive}</p>
      </div>

      {/* Activity Trend */}
      <div>
        <h4 className="mb-2 text-sm font-medium">활동 추이 (4주)</h4>
        <div className="flex gap-1">
          {Object.entries(analysis.activity_trend).map(([week, value]) => (
            <div key={week} className="flex-1">
              <div
                className="rounded bg-primary"
                style={{
                  height: `${Math.max(value * 10, 4)}px`,
                }}
              />
              <p className="mt-1 text-center text-xs text-muted-foreground">
                W{week.slice(-1)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Factors */}
      <div>
        <h4 className="mb-2 text-sm font-medium">위험 요인</h4>
        <div className="space-y-2">
          {analysis.factors.map((factor, index) => (
            <div
              key={index}
              className="flex items-start gap-2 rounded-lg bg-muted p-2"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />
              <div>
                <p className="text-sm font-medium">{factor.factor}</p>
                <p className="text-xs text-muted-foreground">
                  {factor.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div>
        <h4 className="mb-2 text-sm font-medium">권장 사항</h4>
        <div className="space-y-2">
          {analysis.recommendations.map((rec, index) => (
            <div
              key={index}
              className="flex items-start justify-between rounded-lg bg-muted p-2"
            >
              <div>
                <p className="text-sm font-medium">{rec.action}</p>
                <p className="text-xs text-muted-foreground">{rec.reason}</p>
              </div>
              <Badge
                variant={
                  rec.priority === 'high'
                    ? 'danger'
                    : rec.priority === 'medium'
                    ? 'warning'
                    : 'secondary'
                }
              >
                {rec.priority}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      {/* Metadata */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>분석됨: {formatDateTime(analysis.analyzed_at)}</span>
        {analysis.cached && <Badge variant="secondary">캐시됨</Badge>}
      </div>
    </div>
  )
}
